import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { Menu, X } from "lucide-react";

export const NAV_LINKS: [string, string, string][] = [
  ["/#so-funktioniert", "#so-funktioniert", "So funktioniert es"],
  ["/liftarten", "#liftarten", "Liftarten"],
  ["/faq", "#faq", "FAQ"],
  ["/kontakt", "#kontakt", "Kontakt"],
];

const FOCUS_RING =
  "outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-sm";

export function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);

  const isOnepager = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Highlight the currently visible section on the desktop onepager.
  useEffect(() => {
    if (!isOnepager) {
      setActiveSection(null);
      return;
    }
    const ids = NAV_LINKS.map(([, hash]) => hash.slice(1));
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActiveSection(visible.target.id);
      },
      { rootMargin: "-20% 0px -65% 0px", threshold: 0 }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [isOnepager, location.pathname]);

  // Lock background scroll while the mobile menu is open.
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all ${
        scrolled
          ? "bg-background/85 backdrop-blur-md border-b border-border"
          : "bg-background/60 backdrop-blur-sm border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex h-16 items-center justify-between">
        <button
          onClick={() => navigate("/")}
          aria-label="Liftblick Startseite"
          className={`flex items-center ${FOCUS_RING}`}
        >
          <Logo />
        </button>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Hauptnavigation">
          {NAV_LINKS.map(([href, hash, label]) => {
            const isHashOnly = href.startsWith("/#");
            const active = isOnepager
              ? activeSection === hash.slice(1)
              : location.pathname === href;
            // Echte Seiten immer verlinken; nur Ankerziele ohne eigene Seite scrollen.
            const target = isOnepager && isHashOnly ? hash : href;
            return (
              <Link
                key={href}
                to={target}
                aria-current={active ? "page" : undefined}
                className={`relative text-[13.5px] py-1 transition-colors ${FOCUS_RING} ${
                  active ? "text-foreground font-medium" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {label}
                <span
                  aria-hidden
                  className={`absolute -bottom-0.5 left-0 h-px w-full origin-left bg-foreground transition-transform duration-300 ${
                    active ? "scale-x-100" : "scale-x-0"
                  }`}
                />
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-1.5">
          <Link
            to="/fuer-anbieter"
            className={`hidden sm:inline-flex items-center h-10 px-4 text-[13.5px] text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors ${FOCUS_RING}`}
          >
            Für Anbieter
          </Link>
          <Button
            onClick={() => navigate("/wizard")}
            className="h-10 px-5 rounded-sm text-[13.5px] font-medium transition-transform active:scale-[0.98]"
          >
            Liftanfrage erfassen
          </Button>
          <button
            className={`lg:hidden inline-flex h-11 w-11 items-center justify-center -mr-2 text-foreground ${FOCUS_RING}`}
            onClick={() => setMobileMenuOpen((v) => !v)}
            aria-label="Menü"
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-border bg-background">
          <nav
            className="max-w-7xl mx-auto px-6 py-2 flex flex-col"
            aria-label="Mobilnavigation"
          >
            {[...NAV_LINKS.map(([href, , label]) => [href, label] as [string, string]), ["/fuer-anbieter", "Für Anbieter"] as [string, string]].map(
              ([href, label]) => (
                <Link
                  key={href}
                  to={href}
                  className="flex min-h-[48px] items-center border-b border-border/70 text-[15px] text-foreground last:border-b-0"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {label}
                </Link>
              )
            )}
            <Button
              onClick={() => {
                setMobileMenuOpen(false);
                navigate("/wizard");
              }}
              className="my-4 h-12 w-full rounded-sm text-[15px] font-medium"
            >
              Liftanfrage erfassen
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
}
